/**
 * Statutory committee catalogue. Slug used at /compliance/committees/:slug.
 */

export interface CommitteeMember {
  role: string;
  name: string;
  affiliation?: string;
}

export interface Committee {
  slug: string;
  name: string;
  /** Statutory authority that mandates this committee. */
  mandate: string;
  purpose: string;
  chair: string;
  convener: string;
  members: string;
  charter: string[];
  fullMembers: CommitteeMember[];
  /** Step-by-step process to file a complaint. */
  reporting: string[];
  /** Anonymously-fileable? */
  anonymous: boolean;
  /** Recent meetings / actions. */
  activity: Array<{ date: string; action: string }>;
  contact: { email: string; phone: string; helpline?: string };
}

export const COMMITTEES: Committee[] = [
  {
    slug: "anti-ragging",
    name: "Anti-Ragging Committee",
    mandate: "UGC Regulations · 2009",
    purpose: "Prevents and addresses ragging in any form, on or off campus, in compliance with UGC Regulations 2009.",
    chair: "Principal",
    convener: "Dr. Faculty-04",
    members: "12 members including 2 student representatives, 2 wardens, 1 senior faculty per branch, and a designated police liaison.",
    charter: [
      "Conduct anti-ragging awareness sessions for all incoming students within 7 days of academic-year start",
      "Maintain a 24×7 anonymous reporting hotline staffed by trained crisis counsellors",
      "Investigate every reported incident within 24 hours; concluded action within 7 days",
      "Submit a quarterly compliance report to UGC and the Karnataka State Council for Higher Education",
      "Issue an anti-ragging affidavit (signed by student + parent) at admission, per UGC format",
    ],
    fullMembers: [
      { role: "Chair", name: "Principal · Dr. H.B. Aravind" },
      { role: "Convener", name: "Dr. Faculty-04", affiliation: "Senior Professor · CSE" },
      { role: "Member · Boys' hostel warden", name: "Mr. Warden-01" },
      { role: "Member · Girls' hostel warden", name: "Mrs. Warden-02" },
      { role: "Member · Police liaison", name: "Inspector · Davanagere City PS" },
      { role: "Student representative · Boys", name: "Student-Lead-20" },
      { role: "Student representative · Girls", name: "Student-Lead-21" },
      { role: "Faculty representative · CSE", name: "Faculty-02" },
      { role: "Faculty representative · ECE", name: "Faculty-40" },
      { role: "Faculty representative · MECH", name: "Faculty-71" },
      { role: "Faculty representative · CIVIL", name: "Faculty-50" },
      { role: "Faculty representative · MBA", name: "Faculty-78" },
    ],
    reporting: [
      "Call the 24×7 anti-ragging helpline · 1800-180-5522 (UGC) or +91 8192 233 870 (BIET)",
      "Or email antiragging@bietdvg.edu — reports are confidential and reviewed within 24 hours",
      "Or use the UGC online complaint portal at antiragging.in",
      "Or drop a written note (anonymous accepted) into any of 12 reporting boxes across campus",
    ],
    anonymous: true,
    activity: [
      { date: "Mar 2026", action: "Quarterly review · 0 ragging incidents reported in Q1" },
      { date: "Aug 2025", action: "Anti-ragging awareness session for 1,142 incoming students" },
      { date: "Aug 2025", action: "Affidavit collection completed for 100% of fresh students" },
    ],
    contact: { email: "antiragging@bietdvg.edu", phone: "+91 8192 233 870", helpline: "1800-180-5522" },
  },
  {
    slug: "icc",
    name: "Internal Complaints Committee (ICC)",
    mandate: "Sexual Harassment of Women at Workplace Act · POSH 2013",
    purpose: "Handles complaints of sexual harassment as per the POSH Act, 2013. All complaints are confidential.",
    chair: "Senior woman faculty",
    convener: "Dr. Faculty-12",
    members: "7 members including 4 women faculty, 1 NGO representative, and 2 student members.",
    charter: [
      "Investigate complaints within 90 days as mandated by the POSH Act",
      "Maintain strict confidentiality of complainant and witnesses throughout investigation",
      "Conduct annual gender-sensitisation workshops for all faculty, staff and students",
      "Submit annual report on cases received and resolved (anonymised) to the BIET governing body and the District Officer",
    ],
    fullMembers: [
      { role: "Chair · Presiding Officer", name: "Dr. Faculty-12", affiliation: "Senior woman faculty · ECE" },
      { role: "Convener", name: "Dr. Faculty-31", affiliation: "Faculty · MBA" },
      { role: "Member · external NGO representative", name: "Counsellor · Ladies Voluntary Organisation" },
      { role: "Faculty member", name: "Faculty-60", affiliation: "BT" },
      { role: "Faculty member", name: "Faculty-25", affiliation: "EEE" },
      { role: "Student member · UG", name: "Student-Lead-22" },
      { role: "Student member · PG", name: "Student-Lead-23" },
    ],
    reporting: [
      "Email icc@bietdvg.edu — only the ICC Presiding Officer reads incoming mail",
      "Or call +91 8192 233 871 (Convener's direct line · confidential)",
      "Or request an in-person meeting at the ICC office (Block A · Room 7) — no appointment needed",
      "Written complaints can be submitted in a sealed envelope addressed to the Presiding Officer",
    ],
    anonymous: false,
    activity: [
      { date: "Mar 2026", action: "Annual gender-sensitisation workshop · 220 staff attended" },
      { date: "Jan 2026", action: "Q4 2025 report submitted to BIET governing body" },
    ],
    contact: { email: "icc@bietdvg.edu", phone: "+91 8192 233 871" },
  },
  {
    slug: "grievance",
    name: "Grievance Redressal Cell",
    mandate: "AICTE Regulations · 2019",
    purpose: "Addresses academic and administrative grievances raised by students, faculty and staff.",
    chair: "Dean (Academics)",
    convener: "Dr. Faculty-09",
    members: "9 members covering academics, examinations, hostels, finance and student welfare.",
    charter: [
      "First-level resolution within 7 working days; escalation to AICTE if unresolved within 30 days",
      "Cover academic, administrative, examination, hostel, mess and finance-related grievances",
      "Maintain a transparent log of grievances received and their resolution status",
      "Quarterly review of process effectiveness by the Director's office",
    ],
    fullMembers: [
      { role: "Chair", name: "Dean (Academics) · Dr. R. Nagaraj" },
      { role: "Convener", name: "Dr. Faculty-09" },
      { role: "Member · Academics", name: "Faculty-01" },
      { role: "Member · Examinations", name: "Faculty-15" },
      { role: "Member · Hostels", name: "Mr. Warden-01" },
      { role: "Member · Finance", name: "Bursar's office representative" },
      { role: "Member · Student welfare", name: "Dean (Student Affairs) · Dr. M. Lakshmi" },
      { role: "Student member · UG", name: "Student-Lead-24" },
      { role: "Student member · PG", name: "Student-Lead-25" },
    ],
    reporting: [
      "File via the online portal at grievance.bietdvg.edu (BIET sign-in required)",
      "Or email grievance@bietdvg.edu",
      "Or drop a written complaint with the Dean (Academics) office",
    ],
    anonymous: false,
    activity: [
      { date: "Mar 2026", action: "Q1 review · 14 grievances received, 13 resolved within timeline" },
      { date: "Feb 2026", action: "Process improvement: digital tracking with email notifications" },
    ],
    contact: { email: "grievance@bietdvg.edu", phone: "+91 8192 233 872" },
  },
  {
    slug: "sc-st",
    name: "SC/ST Cell",
    mandate: "UGC SC/ST Cell Regulations",
    purpose: "Ensures welfare and protection of SC/ST students and staff; addresses caste-related grievances.",
    chair: "Dean (Student Affairs)",
    convener: "Prof. Faculty-22",
    members: "5 members including SC/ST faculty representatives and student liaison.",
    charter: [
      "Implement reservation policies for admissions, scholarships and recruitment",
      "Provide academic support and bridge courses for SC/ST students",
      "Investigate caste-related complaints within 15 days",
      "Coordinate scholarships disbursement with state and central agencies",
    ],
    fullMembers: [
      { role: "Chair", name: "Dean (Student Affairs) · Dr. M. Lakshmi" },
      { role: "Convener", name: "Prof. Faculty-22" },
      { role: "Member · SC/ST faculty", name: "Faculty-72" },
      { role: "Member · SC/ST faculty", name: "Faculty-44" },
      { role: "Member · Student liaison", name: "Student-Lead-26" },
    ],
    reporting: [
      "Email scst@bietdvg.edu",
      "Or schedule a confidential meeting with the Convener · +91 8192 233 873",
    ],
    anonymous: false,
    activity: [
      { date: "Feb 2026", action: "Bridge course for incoming SC/ST students completed" },
      { date: "Dec 2025", action: "Scholarship disbursement coordinated for 218 students" },
    ],
    contact: { email: "scst@bietdvg.edu", phone: "+91 8192 233 873" },
  },
  {
    slug: "womens-cell",
    name: "Women's Cell",
    mandate: "UGC Saksham guidelines",
    purpose: "Promotes gender equity, organizes awareness programs and supports women students and staff.",
    chair: "Senior woman faculty",
    convener: "Dr. Faculty-31",
    members: "8 women faculty members across departments and 2 student representatives.",
    charter: [
      "Conduct gender-sensitisation programmes for all stakeholders",
      "Organise workshops on safety, self-defence and career guidance for women students",
      "Coordinate with the ICC on POSH-related awareness",
      "Maintain a women's mentorship programme pairing senior women faculty with junior women staff",
    ],
    fullMembers: [
      { role: "Chair", name: "Dr. Faculty-12" },
      { role: "Convener", name: "Dr. Faculty-31" },
      { role: "Member · CSE", name: "Faculty-03" },
      { role: "Member · ECE", name: "Faculty-42" },
      { role: "Member · MECH", name: "Faculty-72" },
      { role: "Member · CIVIL", name: "Faculty-52" },
      { role: "Member · BT", name: "Faculty-60" },
      { role: "Member · MBA", name: "Faculty-78" },
      { role: "Student rep · UG", name: "Student-Lead-27" },
      { role: "Student rep · PG", name: "Student-Lead-28" },
    ],
    reporting: [
      "Email womenscell@bietdvg.edu",
      "Or visit the cell office (Block B · Room 12) Mon-Fri · 11 AM – 4 PM",
    ],
    anonymous: false,
    activity: [
      { date: "Mar 2026", action: "International Women's Day programme · 350 attendees" },
      { date: "Jan 2026", action: "Self-defence workshop · 80 women students participated" },
    ],
    contact: { email: "womenscell@bietdvg.edu", phone: "+91 8192 233 874" },
  },
  {
    slug: "equal-opportunity",
    name: "Equal Opportunity Cell",
    mandate: "UGC Equal Opportunity Cell guidelines · 2012",
    purpose: "Implements equal opportunity policies for differently-abled students, minorities and economically weaker sections.",
    chair: "Principal",
    convener: "Dr. Faculty-18",
    members: "6 members covering accessibility, scholarships and academic support.",
    charter: [
      "Ensure campus accessibility per the Rights of Persons with Disabilities Act, 2016",
      "Coordinate accommodations and assistive support for differently-abled students",
      "Administer scholarships for minorities and economically weaker sections",
      "Annual accessibility audit of campus infrastructure",
    ],
    fullMembers: [
      { role: "Chair", name: "Principal" },
      { role: "Convener", name: "Dr. Faculty-18" },
      { role: "Member · Accessibility", name: "Mr. Campus-Engineer-01" },
      { role: "Member · Scholarships", name: "Mr. Bursar" },
      { role: "Member · Academic support", name: "Faculty-10" },
      { role: "Member · Student liaison", name: "Student-Lead-29" },
    ],
    reporting: [
      "Email eoc@bietdvg.edu",
      "Or schedule a meeting with the Convener · +91 8192 233 875",
    ],
    anonymous: false,
    activity: [
      { date: "Feb 2026", action: "Annual accessibility audit · ramps installed at 4 entry points" },
      { date: "Aug 2025", action: "Scholarships disbursed to 142 students under Equal Opportunity scheme" },
    ],
    contact: { email: "eoc@bietdvg.edu", phone: "+91 8192 233 875" },
  },
];

export function getCommittee(slug: string): Committee | undefined {
  return COMMITTEES.find((c) => c.slug === slug);
}
